import type { ComponentProps, KeyboardEvent, Ref } from 'react';
import type { ClientWithTasks } from '../../types/models';
import {
  activeTasks,
  clientMeta,
  projectName,
  sortByDue,
} from '../../lib/selectors';
import { TaskRow } from '../TaskRow/TaskRow';
import { Avatar } from '../Avatar';
import { DragHandle, PencilIcon } from '../Icon';
import styles from './ClientCard.module.css';

const INLINE_LIMIT = 4;

interface ClientCardProps {
  client: ClientWithTasks;
  onOpen: () => void;
  onEdit: () => void;
  onToggleTask: (taskId: string) => void;
  onAddTask: (title: string) => void;
  onCursor: (title: string) => void;
  onSelectTask: (taskId: string) => void;
  onToggleLineup: (taskId: string) => void;
  /** Drag activator props (from a sortable wrapper), spread onto the handle. */
  dragHandleProps?: ComponentProps<'button'>;
  dragHandleRef?: Ref<HTMLButtonElement>;
}

export function ClientCard({
  client,
  onOpen,
  onEdit,
  onToggleTask,
  onAddTask,
  onCursor,
  onSelectTask,
  onToggleLineup,
  dragHandleProps,
  dragHandleRef,
}: ClientCardProps) {
  const active = activeTasks(client);
  const sorted = sortByDue(active);
  const inline = sorted.slice(0, INLINE_LIMIT);
  const more = active.length - inline.length;

  const handleAdd = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      onAddTask(e.currentTarget.value);
      e.currentTarget.value = '';
    }
  };

  return (
    <div className={styles.card}>
      <div className={styles.head}>
        <Avatar client={client} size={40} />
        <button type="button" className={styles.headText} onClick={onOpen}>
          <div className={styles.name}>{client.name}</div>
          <div className={styles.meta}>{clientMeta(client)}</div>
        </button>
        <button
          type="button"
          className={styles.edit}
          title="Edit client"
          aria-label={`Edit ${client.name}`}
          onClick={onEdit}
        >
          <PencilIcon size={15} />
        </button>
        <button
          type="button"
          ref={dragHandleRef}
          className={styles.drag}
          title="Drag to reorder"
          aria-label={`Drag to reorder ${client.name}`}
          {...dragHandleProps}
        >
          <DragHandle size={16} />
        </button>
      </div>

      {client.projects.length > 0 && (
        <div className={styles.chips}>
          {client.projects.map((p) => (
            <span key={p.id} className={styles.projectChip}>
              <span className={styles.chipDot} />
              {p.name}
            </span>
          ))}
        </div>
      )}

      <div className={styles.tasks}>
        {inline.map((task) => (
          <TaskRow
            key={task.id}
            task={task}
            projectName={projectName(client, task.projectId)}
            onToggle={() => onToggleTask(task.id)}
            onCursor={() => onCursor(task.title)}
            onOpen={() => onSelectTask(task.id)}
            onToggleLineup={() => onToggleLineup(task.id)}
          />
        ))}
        {active.length === 0 && (
          <div className={styles.allClear}>✓ All clear — nothing open</div>
        )}
      </div>

      <div className={styles.footer}>
        {more > 0 && (
          <button type="button" className={styles.viewAll} onClick={onOpen}>
            View all {active.length} →
          </button>
        )}
        <input
          type="text"
          onKeyDown={handleAdd}
          placeholder="+ add a task…"
          className={styles.addInput}
          aria-label={`Add a task to ${client.name}`}
        />
      </div>
    </div>
  );
}
